// Alaine's Compass content — profile-aware commercialization guidance.
// All content is static/client-side; drafts save to localStorage.

export const PROFILES = [
  {
    id: "dry-seasoning",
    tag: "DRY SEASONING",
    name: "Dry seasonings & spice blends",
    blurb: "Particle size, blend uniformity, caking, allergen carryover, and pouch/jar fill accuracy.",
    img: "profile-dry-seasoning.png",
  },
  {
    id: "sauce",
    tag: "SAUCE",
    name: "Hot sauces, BBQ & simmer sauces",
    blurb: "pH, acidified-food status, thermal process, particulates, viscosity, and closure control.",
    img: "profile-sauce.png",
  },
  {
    id: "refrigerated-dip",
    tag: "REFRIGERATED DIP",
    name: "Refrigerated dips & spreads",
    blurb: "Cold chain, RTE micro risk, Listeria controls, shelf life, dairy/sesame/nut allergens.",
    img: "profile-refrigerated-dip.png",
  },
  {
    id: "salsa",
    tag: "SALSA",
    name: "Fresh salsa & pico",
    blurb: "Fresh vs shelf-stable path, pH, produce handling, refrigerated shelf life, or acidified process.",
    img: "profile-salsa.png",
  },
  {
    id: "dressing",
    tag: "DRESSING",
    name: "Dressings, marinades & vinaigrettes",
    blurb: "Emulsion stability, oil separation, acid levels, refrigerated vs shelf-stable decisions.",
    img: "profile-dressing.png",
  },
  {
    id: "frozen",
    tag: "FROZEN",
    name: "Frozen meals, sauces & sides",
    blurb: "Freezer stability, reheating directions, package seal, allergen and validation needs.",
    img: "profile-frozen.png",
  },
  {
    id: "savory-snack",
    tag: "SAVORY SNACK",
    name: "Savory snacks & mixes",
    blurb: "Seasoning adhesion, rancidity, breakage, nitrogen flush, allergen carryover.",
    img: "profile-savory-snack.png",
  },
  {
    id: "meal-kit",
    tag: "MEAL KIT",
    name: "Meal kits & prepared components",
    blurb: "Multiple components, lot tracking, mixed storage types, and assembly controls.",
    img: "profile-meal-kit.png",
  },
];

export const PHASES = [
  {
    n: 1,
    name: "Product development brief",
    desc: "Define profile, channel, claims and first sellable unit.",
    what: "Locking down exactly what you are making, who will buy it, and what claims you will put on the package.",
    why: "Without a tight brief, formulators will chase the wrong taste profile and co-mans will quote the wrong process.",
    preparation: "You need a clear idea of your target consumer, where they shop, and what makes your product different.",
    checklist: [
      "Select product profile (e.g. sauce, dry seasoning)",
      "Determine storage type (refrigerated, shelf-stable, frozen)",
      "Identify target sales channels (retail, DTC, wholesale)",
      "List known allergens and non-negotiable claims (e.g. gluten-free)"
    ],
    templates: ["savory-profile-intake"]
  },
  {
    n: 2,
    name: "Formulation",
    desc: "Make the formula repeatable before quote requests.",
    what: "Converting your kitchen recipe into a commercial formula scaled by weight (grams/ounces) and percentages.",
    why: "An accurate manufacturing quote requires a documented formula by weight, not 'a dash of salt' or '3 cups of flour'.",
    preparation: "Your best kitchen recipe, a kitchen scale that measures in grams, and a spreadsheet.",
    checklist: [
      "Convert all volume measurements to weight (grams)",
      "Calculate ingredient percentages (must equal 100%)",
      "Document the exact order of addition and mixing times",
      "Identify the critical quality points (what ruins the batch if done wrong)"
    ],
    templates: ["commercial-formula-method-sheet"]
  },
  {
    n: 3,
    name: "Costing",
    desc: "Estimate unit, case, package, testing, freight, and first-run cash.",
    what: "Building a realistic financial model of what one unit costs to produce, pack, and ship.",
    why: "Many food businesses fail because they price their product based on the competition without knowing their true costs.",
    preparation: "Ingredient prices, estimated packaging costs, and a rough idea of how many units you will make in a batch.",
    checklist: [
      "Calculate total ingredient cost per batch",
      "Calculate packaging cost per unit (jars, lids, labels, boxes)",
      "Include a realistic labor rate for your own time",
      "Determine the true break-even price for wholesale and DTC"
    ],
    templates: ["first-run-costing-worksheet", "pricing-margin-workbook"]
  },
  {
    n: 4,
    name: "Product testing",
    desc: "Run tests that answer real launch risk.",
    what: "Working with qualified labs or process authorities to determine and verify safety, shelf life, and nutritional content.",
    why: "Buyers and distributors generally require proof of safety and shelf life, and nutrition facts are often required unless you qualify for an exemption.",
    preparation: "A commercially prepared sample of your product in its final packaging, or as close to it as possible.",
    checklist: [
      "Determine required safety tests (pH, water activity, pathogen screen)",
      "Decide on accelerated vs. real-time shelf life testing",
      "Select an accredited or qualified food lab",
      "Review the lab report against your pass/fail criteria"
    ],
    templates: ["testing-brief"]
  },
  {
    n: 5,
    name: "Packaging",
    desc: "Choose package that fits shelf life and line reality.",
    what: "Sourcing primary packaging (the bottle/jar), closures (the lid), labels, and master cases.",
    why: "Packaging dictates the shelf life, the co-man's ability to run the product on their lines, and the final look on the shelf.",
    preparation: "Knowledge of whether your product is hot-filled, cold-filled, or requires a vacuum seal.",
    checklist: [
      "Select a primary package that matches your process (e.g. hot-fill compatible)",
      "Choose a tamper-evident closure mechanism",
      "Verify the co-man has the equipment to apply this specific package and label",
      "Source cases that fit standard pallet dimensions"
    ],
    templates: ["packaging-brief"]
  },
  {
    n: 6,
    name: "Co-manufacturing",
    desc: "Shortlist by process, MOQ, package, allergen, NDA/IP fit.",
    what: "Finding, vetting, and negotiating with a third-party facility to produce your product.",
    why: "A bad co-man relationship can drain your cash and ruin your quality. A good one is a true growth partner.",
    preparation: "Your commercial formula, packaging brief, and first-run costing model.",
    checklist: [
      "Identify facilities with the correct equipment and certifications",
      "Confirm their Minimum Order Quantity (MOQ) matches your budget",
      "Sign a mutual NDA before sharing the full formula",
      "Verify their allergen control program"
    ],
    templates: ["co-man-outreach-email", "co-man-scorecard", "nda-ip-question-checklist"]
  },
  {
    n: 7,
    name: "Regulatory",
    desc: "Turn rules into label, process, facility and claim questions.",
    what: "Identify which product, facility, process, label, and claim requirements apply, then arrange a qualified review.",
    why: "Recalls due to undeclared allergens or unapproved processes can destroy a brand overnight.",
    preparation: "Your final ingredient list, process steps, and any lab test results.",
    checklist: [
      "Ask a qualified process authority which process review or filings apply",
      "Verify facility registrations are up to date",
      "Prepare product identity and net quantity statements for label review",
      "Have allergen declarations reviewed for accuracy and required presentation"
    ],
    templates: ["regulatory-triage-worksheet"]
  },
  {
    n: 8,
    name: "Launch readiness",
    desc: "Lock specs, run calendar, QA release and buyer packet.",
    what: "The final checklist before your first commercial production run and market launch.",
    why: "Missing one detail—like a barcode that won't scan—can delay your launch and upset early buyers.",
    preparation: "Completed tasks from all previous phases.",
    checklist: [
      "Approve final formula, spec sheet, and label proofs",
      "Confirm all packaging materials have arrived at the facility",
      "Set QA release criteria and lot coding format",
      "Assemble the buyer packet (spec sheets, COAs, insurance cert)"
    ],
    templates: ["launch-readiness-checklist"]
  },
];

export const BLOCKED_ITEMS = [
  {
    title: "Supplier documentation",
    desc: "Know which ingredient documents the founder should request.",
  },
  {
    title: "Costing inputs",
    desc: "Identify numbers needed before a realistic first-run estimate.",
  },
  {
    title: "Technical decision",
    desc: "Flag whether this profile needs process, package, testing, or label review.",
  },
];

export const RECENTLY_COMPLETED = [
  { title: "Profile selected", desc: "Profile-specific guidance is active." },
  { title: "Roadmap order set", desc: "Formulation → Costing → Testing." },
  { title: "Templates available", desc: "Every phase has a worksheet." },
];

export const COMAN_CRITERIA = [
  { title: "Category fit", desc: "Already makes similar product" },
  { title: "Reputation", desc: "References, audits, clean communication" },
  { title: "MOQ", desc: "Trial and first real run make sense" },
  { title: "Time zone", desc: "EST/CST/MST/PST affects daily work" },
  { title: "Shipping", desc: "Freight cost and distance" },
];

export const COMAN_GUIDANCE = {
  "dry-seasoning": [
    {
      name: "Small-batch spice co-packers",
      region: "Regional",
      notes:
        "Look for co-packers who already run dry blends: they'll have sifters, blenders, and allergen changeover controls in place. Dry blends ship easily, so you can search beyond your state.",
    },
    {
      name: "Dry-blend specialists",
      region: "National",
      notes:
        "Fit signal is dry-blend experience, allergen controls, lot coding, and willingness to support a small first run. Ask about particle-size capability and fill accuracy for your pouch or jar.",
    },
  ],
  default: [
    {
      name: "Category-fit co-packers",
      region: "Regional",
      notes:
        "Start with co-packers who already make your product type — they have the right equipment, process authority relationships, and allergen program.",
    },
    {
      name: "Process-capable specialists",
      region: "National",
      notes:
        "Verify the process capability your profile needs (thermal, cold-fill, freezing, or blending), and confirm they can run your target package format at trial volume.",
    },
  ],
};

export const COMAN_DOCS = [
  {
    title: "Mutual NDA",
    desc: "Signed before sharing formula, process, supplier list, pricing, or customers.",
  },
  {
    title: "IP ownership addendum",
    desc: "Says you own formula, brand, specs, and improvements unless a separate paid development agreement says otherwise.",
  },
  {
    title: "Manufacturing services agreement",
    desc: "Pricing, payment, forecasts, liability, termination, confidentiality, disputes.",
  },
  {
    title: "Statement of work / quote",
    desc: "Product, package, batch size, line time, setup fees, waste, storage, tests, lead time.",
  },
  {
    title: "Finished product specification",
    desc: "Formula version, sensory target, fill weight, package, shelf life, storage, release criteria.",
  },
  {
    title: "Quality agreement",
    desc: "Allergen control, sanitation, COAs, micro, holds/releases, deviations, complaints.",
  },
  {
    title: "Insurance certificate",
    desc: "Product liability, additional insured if needed, recall coverage if available.",
  },
  {
    title: "Traceability and recall plan",
    desc: "Lot code, mock recall timing, ingredient-to-finished-good records.",
  },
];

export const NDA_IP = [
  {
    title: "Ask first",
    desc: "Before I send formula, process, supplier, or cost information, do you sign mutual NDAs?",
  },
  {
    title: "If they say no",
    desc: "Send only a high-level product brief. Do not send formula percentages, supplier list, or process details.",
  },
  {
    title: "IP clause you want",
    desc: "Brand owner owns formulas, specs, processes, improvements, and work product developed for brand owner, except co-manufacturer background IP.",
  },
  {
    title: "Red flag",
    desc: "They say they own any formula they touch or can sell your formula as private label. Walk away or get counsel.",
  },
];

export const PACKAGING_GUIDANCE = [
  {
    title: "Match package to shelf life",
    desc: "Barrier, closure, and headspace decisions come from your storage type and shelf-life target — not from what looks nice first.",
  },
  {
    title: "Line reality",
    desc: "Confirm your co-man can actually run the format: jar vs pouch vs bottle changes filling equipment, capping, and labeling.",
  },
  {
    title: "Order in stages",
    desc: "Stock packaging for the first runs; custom printed packaging only after formula, fill weight, and label are locked.",
  },
  {
    title: "Tamper evidence",
    desc: "Retail buyers expect tamper-evident closures. Decide shrink band, induction seal, or breakaway cap early.",
  },
];

export const TESTING_GUIDANCE = [
  {
    title: "Test to answer launch risk",
    desc: "Pick tests that answer a real question — shelf life, water activity, pH, micro safety — not a generic panel.",
  },
  {
    title: "Accredited labs",
    desc: "Use ISO 17025-accredited food labs. Your co-man or local extension program can recommend nearby options.",
  },
  {
    title: "Nutrition analysis",
    desc: "Database-calculated nutrition (like Alaine's Bench) is acceptable for most products; lab analysis is needed for fried or unpredictable-moisture products.",
  },
  {
    title: "Keep the reports",
    desc: "Every COA and test report goes into your buyer packet and quality file. Buyers and insurers will ask.",
  },
];

export const REGULATORY_GUIDANCE = [
  {
    title: "Product type drives the rules",
    desc: "Meat/poultry (USDA) vs everything else (FDA), acidified vs not, refrigerated RTE vs shelf-stable — classify first, then the requirements follow.",
  },
  {
    title: "Process authority",
    desc: "Acidified and low-acid foods need a process authority letter and possibly FDA filing (SID). Budget time for this before launch.",
  },
  {
    title: "Facility registration",
    desc: "Your co-man's facility registration and your own business licensing are separate questions — confirm both.",
  },
  {
    title: "Label compliance",
    desc: "Statement of identity, net weight, ingredient list, allergen declaration, nutrition facts, and your business address. Alaine's Bench drafts this for you.",
  },
];

// ---- Pricing & Margin Workbook helpers ----
// Parses a user-typed value into a number; blank/invalid -> 0.
export function toNum(v) {
  if (v === null || v === undefined || v === "") return 0;
  const n = parseFloat(String(v).replace(/[$,%\s]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

// Live calculations for the Pricing & Margin Workbook. Mirrors the Excel
// workbook's formulas: cost per unit, contribution margin, break-even units.
export function computePricingWorkbook(values) {
  const v = (id) => toNum(values[id]);
  const rows = (id) => (Array.isArray(values[id]) ? values[id] : []);

  const unitsPerBatch = v("pm-units-per-batch");
  const currentPrice = v("pm-current-price");
  const proposedPrice = v("pm-proposed-price");
  const currentMonthlyUnits = v("pm-current-monthly-units");
  const dropPct = v("pm-volume-drop") / 100;
  const targetMargin = v("pm-target-margin") / 100;
  const proposedMonthlyUnits = currentMonthlyUnits * (1 - dropPct);

  // Ingredients: purchase cost ÷ purchase size × qty used per batch
  const ingredientBatchCost = rows("pm-ingredients").reduce((sum, r) => {
    const size = toNum(r.size);
    return sum + (size ? (toNum(r.cost) / size) * toNum(r.used) : 0);
  }, 0);
  const ingredientPerUnit = unitsPerBatch ? ingredientBatchCost / unitsPerBatch : 0;

  // Packaging: pack cost ÷ pieces per pack × pieces used per unit
  const packagingPerUnit = rows("pm-packaging").reduce((sum, r) => {
    const pieces = toNum(r.pieces);
    return sum + (pieces ? (toNum(r.cost) / pieces) * toNum(r.used) : 0);
  }, 0);

  // Labor & other
  const laborBatch = v("pm-hours-per-batch") * v("pm-labor-rate");
  const wastePct = v("pm-waste") / 100;
  const laborOtherPerUnit = unitsPerBatch ? (laborBatch + v("pm-other-per-batch")) / unitsPerBatch : 0;
  const sellingPerUnit = (currentMonthlyUnits ? v("pm-samples-month") / currentMonthlyUnits : 0) + v("pm-selling-unit");

  // Cost card
  const subtotal = ingredientPerUnit + packagingPerUnit + laborOtherPerUnit;
  const wasteAllowance = subtotal * wastePct;
  const trueCost = subtotal + wasteAllowance;

  // Price test
  const contribCurrent = currentPrice - trueCost - sellingPerUnit;
  const contribProposed = proposedPrice - trueCost - sellingPerUnit;
  const marginCurrent = currentPrice ? contribCurrent / currentPrice : 0;
  const marginProposed = proposedPrice ? contribProposed / proposedPrice : 0;
  const targetPrice = targetMargin < 1 ? (trueCost + sellingPerUnit) / (1 - targetMargin) : 0;

  // Channels: price × (1 − fees) − true cost
  let channelUnits = 0;
  let channelMonthly = 0;
  let bestChannel = "";
  let bestChannelMonthly = -Infinity;
  rows("pm-channels").forEach((r) => {
    const units = toNum(r.units);
    const contribUnit = toNum(r.price) * (1 - toNum(r.fees) / 100) - trueCost - sellingPerUnit;
    const monthly = units * contribUnit;
    channelUnits += units;
    channelMonthly += monthly;
    if (units > 0 && monthly > bestChannelMonthly) {
      bestChannelMonthly = monthly;
      bestChannel = r.name || "(unnamed channel)";
    }
  });
  const blendedContrib = channelUnits ? channelMonthly / channelUnits : 0;

  // Break-even
  const fixedCosts =
    v("pm-fixed-rent") + v("pm-fixed-insurance") + v("pm-fixed-software") +
    v("pm-fixed-marketing") + v("pm-fixed-admin") + v("pm-fixed-other");
  const operatingProfit = channelMonthly - fixedCosts;
  const breakEvenUnits = blendedContrib > 0 ? fixedCosts / blendedContrib : 0;
  const cushionUnits = channelUnits - breakEvenUnits;

  return {
    proposedMonthlyUnits,
    ingredientBatchCost,
    ingredientPerUnit,
    packagingPerUnit,
    packagingPerBatch: packagingPerUnit * unitsPerBatch,
    laborBatch,
    laborOtherPerUnit,
    subtotal,
    wasteAllowance,
    trueCost,
    sellingPerUnit,
    contribCurrent,
    contribProposed,
    marginCurrent,
    marginProposed,
    targetPrice,
    meetsCurrent: targetMargin > 0 ? (marginCurrent >= targetMargin ? "YES" : "NO") : "—",
    meetsProposed: targetMargin > 0 ? (marginProposed >= targetMargin ? "YES" : "NO") : "—",
    channelUnits,
    channelMonthly,
    blendedContrib,
    bestChannel: bestChannel || "—",
    fixedCosts,
    operatingProfit,
    profitVerdict: operatingProfit > 0 ? "PROFITABLE" : "NOT YET",
    breakEvenUnits,
    cushionUnits,
  };
}

export const PRICING_MARGIN_WORKBOOK = {
  id: "pricing-margin-workbook",
  name: "Pricing & Margin Workbook",
  summary:
    "Know your true cost, test your price, and find your break-even / Setup → ingredients → packaging → labor → cost card → price test → channels → break-even → decision / Green results are calculated for you as you type",
  kind: "workbook",
  intro:
    "Know your true cost, test your price, and find your break-even. Work the sections in order — green results are calculated for you as you type. This is a planning tool, not tax, legal, or accounting advice. Confirm final numbers with a qualified professional.",
  compute: computePricingWorkbook,
  sections: [
    {
      id: "setup",
      title: "1. Product setup",
      intro: "One product at a time. Enter percentages as whole numbers (10 = 10%).",
      fields: [
        { id: "pm-product-name", label: "Product name", type: "text", tip: "One SKU at a time" },
        { id: "pm-units-per-batch", label: "Sellable units per batch", unit: "units", tip: "Only units you can actually sell" },
        { id: "pm-current-price", label: "Current selling price", unit: "$ / unit", tip: "What customers pay today" },
        { id: "pm-proposed-price", label: "Proposed selling price", unit: "$ / unit", tip: "The price you want to test" },
        { id: "pm-current-monthly-units", label: "Current monthly units", unit: "units / mo", tip: "Use a normal recent month" },
        { id: "pm-volume-drop", label: "Expected volume change if price rises", unit: "% drop", tip: "Use 0 if you expect no change" },
        { id: "pm-target-margin", label: "Target contribution margin", unit: "%", tip: "50% is a common food-business target" },
      ],
      results: [
        { key: "proposedMonthlyUnits", label: "Proposed monthly units", format: "number", note: "Current units × (1 − expected drop)" },
      ],
    },
    {
      id: "ingredients",
      title: "2. Ingredient cost",
      intro: "What you pay at the store vs. what one batch actually uses.",
      table: {
        id: "pm-ingredients",
        addLabel: "Add ingredient",
        columns: [
          { id: "name", label: "Ingredient", type: "text" },
          { id: "cost", label: "Purchase cost ($)" },
          { id: "size", label: "Purchase size (qty)" },
          { id: "unit", label: "Unit (oz, lb, each...)", type: "text" },
          { id: "used", label: "Qty used per batch" },
        ],
      },
      results: [
        { key: "ingredientBatchCost", label: "Total ingredient cost per batch", format: "money" },
        { key: "ingredientPerUnit", label: "Ingredient cost per sellable unit", format: "money" },
      ],
    },
    {
      id: "packaging",
      title: "3. Packaging cost",
      intro: "Every bag, jar, lid, label, sticker, and box that ships with one unit.",
      table: {
        id: "pm-packaging",
        addLabel: "Add component",
        columns: [
          { id: "name", label: "Component", type: "text" },
          { id: "cost", label: "Pack cost ($)" },
          { id: "pieces", label: "Pieces per pack" },
          { id: "used", label: "Pieces used per unit" },
        ],
      },
      results: [
        { key: "packagingPerUnit", label: "Total packaging cost per unit", format: "money" },
        { key: "packagingPerBatch", label: "Packaging cost per batch", format: "money" },
      ],
    },
    {
      id: "labor",
      title: "4. Labor & other direct costs",
      intro: "The costs founders most often forget to charge for — including your own time.",
      fields: [
        { id: "pm-hours-per-batch", label: "Hands-on hours per batch", unit: "hours", tip: "Prep, cook, fill, clean-up" },
        { id: "pm-labor-rate", label: "Labor rate (pay yourself!)", unit: "$ / hour", tip: "Free labor hides your real cost" },
        { id: "pm-waste", label: "Additional manufacturing cost contingency", unit: "%", tip: "Use 0 when losses are already included in sellable yield. Do not count the same loss twice." },
        { id: "pm-other-per-batch", label: "Other manufacturing cost per batch", unit: "$ / batch", tip: "Batch kitchen rental, recurring production testing or tolling; exclude selling and one-time development expenses" },
      ],
      results: [
        { key: "laborBatch", label: "Labor cost per batch", format: "money", note: "Hours × rate" },
        { key: "laborOtherPerUnit", label: "Labor & other manufacturing cost per unit", format: "money", note: "(Labor + other manufacturing costs) ÷ sellable units" },
      ],
    },
    {
      id: "cost-card",
      title: "5. Cost card — true cost of one unit",
      intro: "Nothing to enter here. If a number looks wrong, fix it in its own section. Rule of thumb: if your selling price is less than 2× your true cost, wholesale will be very hard.",
      results: [
        { key: "ingredientPerUnit", label: "Ingredients (section 2)", format: "money" },
        { key: "packagingPerUnit", label: "Packaging (section 3)", format: "money" },
        { key: "laborOtherPerUnit", label: "Labor & other (section 4)", format: "money" },
        { key: "subtotal", label: "Subtotal", format: "money" },
        { key: "wasteAllowance", label: "Additional manufacturing contingency", format: "money", note: "Subtotal × contingency %" },
        { key: "trueCost", label: "ESTIMATED MANUFACTURING COGS PER UNIT", format: "money", strong: true },
      ],
    },
    {
      id: "price-test",
      title: "6. Price test",
      intro: "Contribution deducts manufacturing COGS and variable selling expenses. One-time development budgets are recorded separately under planning detail and are not recurring COGS.",
      fields: [
        { id: "pm-samples-month", label: "Samples & giveaways per month", unit: "$ / month", tip: "Selling expense, not manufacturing COGS" },
        { id: "pm-selling-unit", label: "Other variable selling / distribution expense", unit: "$ / unit", tip: "Exclude channel fees entered below and fixed monthly expenses", required: false },
      ],
      results: [
        { key: "sellingPerUnit", label: "Variable selling expense per unit", format: "money" },
        { key: "contribCurrent", label: "Contribution per unit at current price", format: "money", note: "Price − manufacturing COGS − variable selling expense" },
        { key: "marginCurrent", label: "Contribution margin at current price", format: "percent" },
        { key: "meetsCurrent", label: "Current price meets your target margin?", format: "text" },
        { key: "contribProposed", label: "Contribution per unit at proposed price", format: "money" },
        { key: "marginProposed", label: "Contribution margin at proposed price", format: "percent" },
        { key: "meetsProposed", label: "Proposed price meets your target margin?", format: "text" },
        { key: "targetPrice", label: "Target price for your margin goal", format: "money", strong: true, note: "(Manufacturing COGS + variable selling expense) ÷ (1 − target margin); channel fees not included" },
      ],
    },
    {
      id: "channels",
      title: "7. Where do you actually make money?",
      intro: "Same product, different math per channel. Wholesale example: a $9 retail unit often sells to the store at 50% off — enter $4.50 as your price, not $9.",
      table: {
        id: "pm-channels",
        addLabel: "Add channel",
        starterRows: [{ name: "Farmers market" }, { name: "Online / direct" }, { name: "Wholesale / retail" }],
        columns: [
          { id: "name", label: "Channel", type: "text" },
          { id: "units", label: "Monthly units" },
          { id: "price", label: "Your price in this channel ($)" },
          { id: "fees", label: "Fees / discount %" },
        ],
      },
      results: [
        { key: "channelUnits", label: "Total monthly units", format: "number" },
        { key: "channelMonthly", label: "Total monthly contribution", format: "money", note: "Per channel: (price × (1 − fees) − COGS − variable selling expense) × units" },
        { key: "blendedContrib", label: "Blended contribution per unit", format: "money" },
      ],
    },
    {
      id: "break-even",
      title: "8. Break-even",
      intro: "Fixed costs happen whether you sell one unit or a thousand.",
      fields: [
        { id: "pm-fixed-rent", label: "Kitchen / storage rent", unit: "$ / month", tip: "Commissary, storage unit" },
        { id: "pm-fixed-insurance", label: "Insurance & licenses", unit: "$ / month", tip: "Required to operate" },
        { id: "pm-fixed-software", label: "Software & bookkeeping", unit: "$ / month", tip: "Website, accounting, email" },
        { id: "pm-fixed-marketing", label: "Marketing baseline", unit: "$ / month", tip: "Ads, market booth fees" },
        { id: "pm-fixed-admin", label: "Owner admin pay", unit: "$ / month", tip: "Your non-production time" },
        { id: "pm-fixed-other", label: "Other fixed costs", unit: "$ / month", tip: "Anything recurring" },
      ],
      results: [
        { key: "fixedCosts", label: "Total fixed costs", format: "money" },
        { key: "operatingProfit", label: "Operating profit", format: "money", note: "Monthly contribution − fixed costs" },
        { key: "profitVerdict", label: "Monthly result", format: "text" },
        { key: "breakEvenUnits", label: "Break-even units per month", format: "number", strong: true, note: "Fixed costs ÷ blended contribution per unit" },
        { key: "cushionUnits", label: "Units above / below break-even", format: "number", note: "Positive = cushion, negative = gap" },
      ],
    },
    {
      id: "decision",
      title: "9. The decision",
      intro: "One screen. Trust it only as much as you trust your inputs. Keep, raise, rework, or pause?",
      results: [
        { key: "trueCost", label: "Manufacturing COGS per unit", format: "money", note: "Also allow for selling expenses, channel fees and fixed costs when setting prices" },
        { key: "targetPrice", label: "What price hits your target margin?", format: "money", note: "Compare to what customers will actually pay" },
        { key: "bestChannel", label: "Which channel earns the most?", format: "text" },
        { key: "breakEvenUnits", label: "How many units keep the lights on?", format: "number" },
        { key: "profitVerdict", label: "Are you profitable at today's volume?", format: "text" },
      ],
      fields: [
        { id: "pm-decision", label: "Your decision", type: "text", tip: "Keep the price, raise it, rework the recipe/packaging, or pause and rethink" },
      ],
    },
  ],
};

export const TEMPLATES = [
  {
    id: "savory-profile-intake",
    name: "Product Development Brief",
    summary:
      "Product profile: dry seasoning, sauce, dip, salsa, dressing, frozen, snack, or meal kit / Storage type: shelf-stable, refrigerated, frozen, or made-to-order / First sales channel: DTC, retail, foodservice, farmers market, wholesale, or distributor",
    fields: [
      "Product profile: dry seasoning, sauce, dip, salsa, dressing, frozen, snack, or meal kit",
      "Storage type: shelf-stable, refrigerated, frozen, or made-to-order",
      "First sales channel: DTC, retail, foodservice, farmers market, wholesale, or distributor",
      "Target package size and serving/use occasion",
      "Known allergens or sensitive ingredients",
      "Claims the founder wants to make",
      "What is already known and what is still unknown",
      "Next decision needed before formulation work",
    ],
  },
  {
    id: "commercial-formula-method-sheet",
    name: "Commercial Formula Method Sheet",
    summary:
      "Formula version and date / Ingredient list by weight and percentage / Supplier name and spec needed for each ingredient",
    fields: [
      "Formula version and date",
      "Ingredient list by weight and percentage",
      "Supplier name and spec needed for each ingredient",
      "Batch method: order of addition, mix times, temperatures",
      "Critical points: what ruins the batch if done wrong",
      "Target yield and expected loss",
    ],
  },
  {
    id: "first-run-costing-worksheet",
    name: "First-Run Costing Worksheet",
    summary:
      "Product profile and storage type / Target package size and units per case / Formula ingredient cost per unit",
    fields: [
      "Product profile and storage type",
      "Target package size and units per case",
      "Formula ingredient cost per unit",
      "Packaging cost per unit (container, closure, label, case)",
      "Co-man tolling fee or setup fees",
      "Testing and regulatory costs for the run",
      "Freight in and freight out estimates",
      "Total first-run cash needed",
    ],
  },
  PRICING_MARGIN_WORKBOOK,
  {
    id: "testing-brief",
    name: "Testing Brief",
    summary:
      "Product profile and storage type / Safety question to answer / Quality/shelf-life question to answer",
    fields: [
      "Product profile and storage type",
      "Safety question to answer",
      "Quality/shelf-life question to answer",
      "Tests requested (pH, aw, micro, shelf life, nutrition)",
      "Lab selected and turnaround time",
      "Pass/fail criteria before launch",
    ],
  },
  {
    id: "packaging-brief",
    name: "Packaging Brief",
    summary: "Primary package and closure / Fill method and temperature / Tamper evidence",
    fields: [
      "Primary package and closure",
      "Fill method and temperature",
      "Tamper evidence",
      "Label material and application method",
      "Case pack and pallet configuration",
      "Supplier and lead time",
    ],
  },
  {
    id: "co-man-outreach-email",
    name: "Co-Man Outreach Email",
    summary:
      "Subject: Co-packing inquiry for [product type] - [package] - [first run] / Product: storage type, allergens, particulates/viscosity / Package: size, material, closure, case pack",
    fields: [
      "Subject: Co-packing inquiry for [product type] - [package] - [first run]",
      "Product: storage type, allergens, particulates/viscosity",
      "Package: size, material, closure, case pack",
      "First run size and 12-month volume estimate",
      "What you need from them: trial run, MOQ, tolling quote",
      "NDA question: do you sign mutual NDAs before formula details?",
    ],
  },
  {
    id: "regulatory-triage-worksheet",
    name: "Regulatory Triage Worksheet",
    summary:
      "Product type and storage type / Meat/poultry/dairy/seafood/allergen flags / Acidified or low-acid possibility",
    fields: [
      "Product type and storage type",
      "Meat/poultry/dairy/seafood/allergen flags",
      "Acidified or low-acid possibility",
      "Process authority needed? (yes/no/unsure)",
      "Facility registrations required",
      "Label claims that need substantiation",
    ],
  },
  {
    id: "launch-readiness-checklist",
    name: "Launch Readiness Checklist",
    summary:
      "Final formula/spec approved / Costing model approved for first run / Testing complete or scheduled with pass/fail decision",
    fields: [
      "Final formula/spec approved",
      "Costing model approved for first run",
      "Testing complete or scheduled with pass/fail decision",
      "Packaging ordered with lead time confirmed",
      "Label proof approved and compliant",
      "QA release criteria and lot coding set",
      "Buyer packet assembled (spec, COA, insurance)",
    ],
  },
  {
    id: "co-man-scorecard",
    name: "Co-Man Scorecard",
    summary: "Product/process fit / Smallest paid trial / first-run MOQ / Package line fit",
    fields: [
      "Product/process fit",
      "Smallest paid trial / first-run MOQ",
      "Package line fit",
      "Allergen program fit",
      "NDA/IP terms acceptable",
      "Communication quality and time zone",
      "Total score and decision",
    ],
  },
  {
    id: "nda-ip-question-checklist",
    name: "NDA + IP Question Checklist",
    summary:
      "Will you sign a mutual NDA before formula details? / Does your agreement state brand owner keeps formula/spec/IP rights? / Who owns improvements made during scale-up?",
    fields: [
      "Will you sign a mutual NDA before formula details?",
      "Does your agreement state brand owner keeps formula/spec/IP rights?",
      "Who owns improvements made during scale-up?",
      "Can you sell or reuse my formula as private label?",
      "Notes and red flags",
    ],
  },
  {
    id: "finished-product-spec",
    name: "Finished Product Spec",
    summary:
      "Product name and version / Formula by weight percentages / Ingredient supplier specs and allergens",
    fields: [
      "Product name and version",
      "Formula by weight percentages",
      "Ingredient supplier specs and allergens",
      "Sensory target (appearance, aroma, flavor, texture)",
      "Fill weight and tolerance",
      "Package, shelf life, storage conditions",
      "Release criteria",
    ],
  },
];
